import { useState, type FormEvent } from "react";
import { useAuth } from "../auth/AuthContext.js";
import { ApiError } from "../lib/api.js";
import { AUTH_CARD_CLASS, AUTH_INPUT_CLASS, AuthShell } from "../components/AuthShell.js";
import { Button } from "../components/Button.js";

export const TotpChallengePage = ({
  challengeId,
  onBack,
}: {
  challengeId: string;
  onBack: () => void;
}): JSX.Element => {
  const { verifyTotp } = useAuth();
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await verifyTotp(challengeId, code);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong. Please try again.");
      setCode("");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell>
      <form onSubmit={handleSubmit} className={AUTH_CARD_CLASS}>
        <div>
          <h1 className="font-heading text-2xl font-semibold">Two-factor authentication</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Enter the 6-digit code from your authenticator app.
          </p>
        </div>

        <div className="space-y-1">
          <label htmlFor="code" className="text-sm font-medium">
            Authentication code
          </label>
          <input
            id="code"
            type="text"
            inputMode="numeric"
            pattern="\d{6}"
            maxLength={6}
            autoComplete="one-time-code"
            required
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            className={`${AUTH_INPUT_CLASS} text-center text-lg tracking-[0.5em]`}
          />
        </div>

        {error && (
          <p
            role="alert"
            className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            {error}
          </p>
        )}

        <Button
          type="submit"
          variant="primary"
          size="lg"
          disabled={submitting || code.length !== 6}
          className="w-full"
        >
          {submitting ? "Verifying…" : "Verify"}
        </Button>

        <button
          type="button"
          onClick={onBack}
          className="w-full rounded-md py-2 text-center text-sm text-muted-foreground underline hover:text-foreground"
        >
          Back to sign in
        </button>
      </form>
    </AuthShell>
  );
};
